import React, { useState } from 'react';
import { Modal, Button } from 'antd';
import { AlertCircle, Download, Expand } from 'lucide-react';
import {
  OrderCard,
  CardContent,
  OrderInfo,
  OrderHeader,
  CustomerName,
  OrderEmail,
  ProductName,
  OrderDetails,
  OrderDate,
  EstimatedCompletion,
  OrderMetrics,
  MetricItem,
  AmountValue,
  StatusTag,
  ActionButton,
  ModalContent,
  CustomerNotes,
  NotesLabel,
  NotesPreview,
  PlatformInfo,
  PlatformTag,
  FeaturesContainer,
  FeatureTag
} from './styled';
import { StyledButton } from '../../../components/Button/styled';

const OrderCardComponent = ({ order, onUpdateStatus, onViewOrder, onViewFulfillment }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);

  const {
    _id,
    customerName,
    customerEmail,
    productName,
    amount,
    status,
    createdAt,
    estimatedCompletion,
    customerNotes,
    notes,
    orderType,
    features = {},
    followPlatform,
    followUsername,
    likesPostLink,
    commentsPlatform,
    commentsPostLink,
    shoutoutType,
    shoutoutPlatform,
    customDetails,
    shoutoutImage
  } = order;

  const calculateEstimatedCompletion = (createdDate) => {
    const date = new Date(createdDate);
    date.setDate(date.getDate() + 3); // Add 3 days
    return date;
  };

  const formatDate = (dateInput) => {
    let date;
    if (typeof dateInput === 'string' || typeof dateInput === 'number') {
      date = new Date(dateInput);
    } else if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      return 'Invalid Date';
    }

    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  };

  const formattedCreatedAt = formatDate(createdAt);
  // Use estimatedCompletion if it exists, otherwise calculate it
  const formattedEstimatedCompletion = estimatedCompletion
    ? formatDate(estimatedCompletion)
    : formatDate(calculateEstimatedCompletion(createdAt));

  const handleUpdateStatus = (e) => {
    e.stopPropagation();
    onUpdateStatus(order);
  };

  const handleViewNotes = (e) => {
    e.stopPropagation();
    setIsModalVisible(true);
  };

  const handleViewFulfillment = (e) => {
    e.stopPropagation();
    onViewFulfillment(order);
  };

  const handleImageClick = (e) => {
    e.stopPropagation();
    setShowImagePreview(true);
  };

  const handleDownloadImage = async (e) => {
    e.stopPropagation();
    try {
      const response = await fetch(shoutoutImage);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `shoutout-image-${_id}.${blob.type.split('/')[1]}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  const renderFeatures = () => {
    if (!features) return null;

    const featureList = [];
    if (features.follows) featureList.push({ type: 'follows', platform: followPlatform });
    if (features.likes) featureList.push({ type: 'likes', platform: followPlatform });
    if (features.comments) featureList.push({ type: 'comments', platform: commentsPlatform });
    if (features.shoutouts) featureList.push({ type: 'shoutouts', platform: shoutoutPlatform });
    if (features.custom) featureList.push({ type: 'custom' });

    return (
      <FeaturesContainer>
        {featureList.map(({ type, platform }) => (
          <FeatureTag key={type} type={type}>
            {type.charAt(0).toUpperCase() + type.slice(1)}
            {platform && ` • ${platform}`}
          </FeatureTag>
        ))}
      </FeaturesContainer>
    );
  };

  const renderEngagementDetails = () => {
    if (!orderType || orderType !== 'engagement') return null;

    return (
      <>
        {features.follows && followUsername && (
          <PlatformInfo>
            <PlatformTag>Username: {followUsername}</PlatformTag>
          </PlatformInfo>
        )}
        {features.likes && likesPostLink && (
          <PlatformInfo>
            <PlatformTag>Post Link: {likesPostLink}</PlatformTag>
          </PlatformInfo>
        )}
        {features.comments && commentsPostLink && (
          <PlatformInfo>
            <PlatformTag>Post Link: {commentsPostLink}</PlatformTag>
          </PlatformInfo>
        )}
        {features.shoutouts && (
          <>
            {shoutoutType && (
              <PlatformInfo>
                <PlatformTag>Type: {shoutoutType}</PlatformTag>
              </PlatformInfo>
            )}
            {shoutoutImage && (
              <PlatformInfo
                style={{
                  marginTop: '12px',
                  position: 'relative',
                  cursor: 'pointer'
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    display: 'inline-block'
                  }}
                  onClick={handleImageClick}
                >
                  <img
                    src={shoutoutImage}
                    alt="Shoutout content"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      borderRadius: '8px',
                      objectFit: 'cover'
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: 'rgba(0,0,0,0.5)',
                      borderRadius: '4px',
                      padding: '4px',
                    }}
                  >
                    <Expand size={20} color="white" />
                  </div>
                </div>
              </PlatformInfo>
            )}
          </>
        )}
      </>
    );
  };

  const getStatusText = () => {
    if (orderType === 'engagement') {
      return status.replace('_', ' ').toUpperCase();
    }
    return status.replace('_', ' ').toUpperCase();
  };

  return (
    <>
      <OrderCard onClick={() => onViewOrder(_id)}>
        <CardContent>
          <OrderInfo>
            <OrderHeader>
              <CustomerName>{customerName}</CustomerName>
              <StatusTag status={status}>
                {getStatusText()}
              </StatusTag>
            </OrderHeader>

            <OrderEmail>{customerEmail}</OrderEmail>

            {orderType === 'engagement' ? (
              <>
                <ProductName>Engagement Service</ProductName>
                {renderFeatures()}
                {renderEngagementDetails()}
              </>
            ) : (
              <ProductName>{productName}</ProductName>
            )}

            <OrderDetails>
              <OrderDate>Order Date: {formattedCreatedAt}</OrderDate>
              <EstimatedCompletion>
                {status === 'fulfilled' || status === 'completed'
                  ? `Fulfilled On: ${formattedEstimatedCompletion}`
                  : `Complete By: ${formattedEstimatedCompletion}`
                }
              </EstimatedCompletion>
              {(customerNotes || customDetails) && (
                <CustomerNotes>
                  <NotesLabel>
                    <AlertCircle size={16} style={{ marginRight: '4px' }} />
                    Customer Request:
                  </NotesLabel>
                  <NotesPreview>
                    {(customerNotes || customDetails).slice(0, 100)}
                    {(customerNotes || customDetails).length > 100 ? '...' : ''}
                  </NotesPreview>
                </CustomerNotes>
              )}
            </OrderDetails>
          </OrderInfo>

          <OrderMetrics>
            <MetricItem>
              <AmountValue>${amount.toFixed(2)}</AmountValue>
            </MetricItem>
            {notes && (
              <MetricItem>
                <ActionButton type="link" onClick={handleViewNotes}>
                  View Notes
                </ActionButton>
              </MetricItem>
            )}
            <MetricItem>
              {status === 'unfulfilled' || status === 'pending' || status === 'processing' ? (
                <StyledButton type="primary" onClick={handleUpdateStatus}>
                  Fulfill Order
                </StyledButton>
              ) : (
                <ActionButton type="default" onClick={handleViewFulfillment}>
                  View Fulfillment
                </ActionButton>
              )}
            </MetricItem>
          </OrderMetrics>
        </CardContent>
      </OrderCard>

      {/* Notes Modal */}
      <Modal
        title="Order Notes"
        open={isModalVisible}
        onOk={() => setIsModalVisible(false)}
        onCancel={() => setIsModalVisible(false)}
      >
        <ModalContent>
          {notes || 'No notes available for this order.'}
        </ModalContent>
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        title="Shoutout Image"
        open={showImagePreview}
        onCancel={() => setShowImagePreview(false)}
        width={800}
        footer={[
          <Button
            key="download"
            type="primary"
            onClick={handleDownloadImage}
            icon={<Download size={16} />}
          >
            Download Image
          </Button>
        ]}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'auto'
        }}>
          <img
            src={shoutoutImage}
            alt="Shoutout content full size"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain'
            }}
          />
        </div>
      </Modal>
    </>
  );
};

export default OrderCardComponent;